import { useTheme } from "next-themes";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { customCancelButton, customModalCancelButton, fundBackLink, stockTimeRangeButton } from "../components/customStyles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { intradayData, historicalData } from "./data";
import dayjs from "dayjs";
import FundPerformanceChart from "../components/fundPerformanceChart";
import useWindowSize from "../hooks/useWindowSize";
import { getNavHistory } from "../services/funds.service";
import retryApi from "../utils/api";
import { useI18n } from "../hooks/useI18n";
import { numberWithCommas, separateNumber } from "../utils/numbers";
import { useTranslation } from "react-i18next";
import { useAuth } from "../store/AuthContext";

const getRiskColor = (value) => {
  if (value === "Low") return "#a3d643";
  else if (value === "Medium") return "#fc8b00";
  else if (value === "High") return "#ff0000";
  else return "#827c83";
};

export function filterTimeSeries(data, range, navDate) {
  // 1) Normalize & sort ascending
  const objs = data
    .map(([ts, price, units, open]) => ({
      ts,
      time: dayjs(ts * 1000),
      price,
      units,
      open,
    }))
    .sort((a, b) => a.time.valueOf() - b.time.valueOf());

  // const now = dayjs();
  let now = navDate ? dayjs(navDate) : dayjs();
  let cutoff;

  switch (range) {
    case "YTD":
      cutoff = now.startOf("year");
      break;

    case "1M":
      cutoff = now.subtract(1, "month");
      break;

    case "6M":
      cutoff = now.subtract(6, "month");
      break;

    case "1Y":
      cutoff = now.subtract(1, "year");
      break;

    case "3Y":
      cutoff = now.subtract(3, "year");
      break;

    case "5Y":
      cutoff = now.subtract(5, "year");
      break;

    default:
      return data;
  }

  // 2) Filter by comparing timestamps directly
  // const startMs = cutoff.valueOf();
  // const endMs = now.valueOf();

  const startMs = cutoff.startOf("day").valueOf();
  const endMs = now.endOf("day").valueOf();

  const filtered = objs
    .filter((pt) => {
      const t = pt.time.valueOf();
      return t >= startMs && t <= endMs;
    })
    .map((pt) => [pt.ts, pt.price, pt.units, pt.open]);

  return filtered;
}

export default function FundDetails({ symbol, closeButton, loading, funds, unitholder, setMenu, isModalOpen }) {
  const [activeTimeRange, setActiveTimeRange] = useState("1M");
  const [chartData, setChartData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(true);
  const { theme } = useTheme();
  // const { symbol } = useParams();
  const { isRTL } = useI18n();
  const { t } = useTranslation();
  const { user } = useAuth()

  const navigate = useNavigate();


  // const { funds, loading, error } = useOutletContext();
  const screenSize = useWindowSize();

  const abortRef = useRef(null);

  const currentFund = funds.find((item) => item.symbol_code === symbol);

  const navDate = dayjs(currentFund?.navData?.nav_date).format("DD-MMM-YYYY");

  const nav = separateNumber(currentFund?.navData?.nav || "0", { format: true, digits: 4 });
  const offer = separateNumber(currentFund?.navData?.offer_price || "0", { format: true, digits: 4 });
  const unitsIssued = separateNumber(currentFund?.navData?.total_fund_units || "0", { format: true, digits: 4 });

  const computeChartData = useCallback(() => {
    // if (activeTimeRange === "1D") {
    //   return intradayData;
    // } else {
    if (!chartData) {
      return null;
    } else {
      const filteredData = filterTimeSeries(chartData, activeTimeRange, currentFund?.navData?.nav_date);
      return filteredData;
    }
    // }
  }, [activeTimeRange, chartData]);

  const finalChartData = computeChartData();

  const hasBalanceUnits = useMemo(() => {
    if (!user) return false;

    if (user && user.requiresDocumentVerification) return false;

    if (unitholder) {
      const parsed = JSON.parse(unitholder?.balance_unit);

      if (parsed.length === 0 || !parsed) return false;

      const hasFundHolding = parsed.find(item => item.fund_code === currentFund?.symbol_code)

      if (!hasFundHolding) return false;

      return true;
    }

    // return true;
  }, [unitholder, user])

  useEffect(() => {
    if (!currentFund) return;

    const getFundsNavHistory = async (retries = 3) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const ac = new AbortController();
      abortRef.current = ac;

      setLoadingChart(true);

      const navDate = currentFund?.navData?.nav_date; // e.g. "2025-08-10T00:00:00.000Z"

      // choose navDate if present and valid, otherwise use now
      let today = navDate ? dayjs(navDate) : dayjs();
      if (!today.isValid()) today = dayjs(); // fallback if input was invalid
      const currentDate = today.format("YYYY-MM-DD");
      const prevYearDate = today.subtract(5, "year").format("YYYY-MM-DD");

      try {
        const res = await retryApi((signal) => getNavHistory(currentFund?.symbol_code, prevYearDate, currentDate, signal), retries, ac.signal);

        const converted = res?.data.data?.map((item) => [dayjs(item.nav_date).unix(), item.nav, item.total_fund_units, item.nav_date]).sort((a, b) => a[0] - b[0]);

        setChartData(converted);

        if (res.error) {
          // console.log("err after retries", res?.data);
        } else {
          // console.log('no errors', monthlyResponse, quarterlyResponse)
        }
      } catch (err) {
        // console.log(err);
      } finally {
        setLoadingChart(false);
      }
    };

    getFundsNavHistory();
  }, [funds]);

  return (
    <div
      className='w-100 p-0 pb-0'
      style={{
        backgroundColor: theme === 'dark' ? 'rgb(42 47 53)' : "var(--theme-bg)",
      }}
    >
      <div className=''>
        {/* <Link
          className='d-inline-flex btn align-items-center'
          to='..'
          relative='path'
          style={{
            ...fundBackLink(theme),
            textDecoration: "none",
            fontSize: "18px",
            gap: "5px",
          }}
        >
          {isRTL ? (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={24}
              height={24}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth={2}
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-right'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M5 12l14 0' />
              <path d='M13 18l6 -6' />
              <path d='M13 6l6 6' />
            </svg>
          ) : (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={24}
              height={24}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth={2}
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-left'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M5 12l14 0' />
              <path d='M5 12l6 6' />
              <path d='M5 12l6 -6' />
            </svg>
          )}
          {t("app.messages.back.funds")}
        </Link> */}
        <div
          className='position-relative px-3 px-sm-3 pt-1 pt-sm-3 p-3 pb-2 pb-sm-4'
          style={{
            height: screenSize <= 575 ? "calc(100dvh - 50px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 60px)" : "calc(100dvh - 120px)",
            overflowY: "auto",
            borderRadius: "28px",
            scrollbarWidth: "none",
          }}
        >
          {loading ? (
            <div
              className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
              style={{
                color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.2rem",
                }}
              >
                {t("app.loading.details")}
              </div>
              <div
                className='spinner-border'
                style={{
                  fontSize: "0.8rem",
                  height: "30px",
                  width: "30px",
                  color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                }}
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div
                className='mb-4'
                style={{
                  display: "grid",
                  gridTemplateColumns: screenSize >= 992 ? "8fr 4fr" : screenSize <= 991 && screenSize >= 576 ? "1fr 1fr" : "1fr",
                  gap: screenSize >= 576 ? '0' : '23px'
                }}
              >
                <div
                  className=''
                  style={{
                    paddingInline: screenSize >= 576 ? "0 6.5px" : "0",
                  }}
                >
                  <div
                    className='row w-100 mx-auto align-items-start justify-content-between p-3 p-sm-4'
                    style={{
                      backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "var(--theme-card-border)",
                    }}
                  >
                    <div
                      className='col-auto p-0'
                      style={{
                        lineHeight: "1.1",
                        // minHeight: screenSize >= 992 ? "90px" : "auto"
                        // backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        // borderRadius: "20px",
                        // border: "1px solid",
                        // borderColor: "var(--theme-card-border)",
                      }}
                    >
                      <div
                        className='mb-2'
                        style={{
                          fontSize: "32px",
                          color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {currentFund.symbol_code}
                      </div>
                      <div
                        className='mb-2'
                        style={{
                          fontSize: "22px",
                          color: theme === "dark" ? "rgba(255,255,255,.6)" : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {currentFund.fund_name}
                      </div>
                    </div>
                    <div className='col-auto p-0'>
                      <label className='glitch-radio-container'>
                        <span className='radio-label'>{t("invest.fund.psx")}</span>
                        <input type='radio' name='protocol' disabled={true} checked={currentFund?.psx_listing == "false" ? false : true} />
                        <div className='radio-circle'>
                          <div className='radio-dot'></div>
                          <div className='pulse' style={{ "--d": "0s" }}></div>
                          <div className='pulse' style={{ "--d": "0.3s" }}></div>
                        </div>
                      </label>
                    </div>
                    <div className='col-12 p-0'>
                      <hr style={{ color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.6)", marginBlock: ".75rem" }} />
                      <div className='d-flex align-items-center justify-content-between'>
                        <div
                          className=''
                          style={{
                            color: theme === "dark" ? "rgba(232, 147, 238, 1)" : "rgb(41, 124, 223)",
                            fontSize: "20px",
                          }}
                        >
                          {currentFund?.nature}
                        </div>
                        <div
                          className=''
                          style={{
                            color: theme === "dark" ? "rgba(255,255,255,.7)" : "rgba(0, 0, 0, 0.7)",
                            fontSize: "17px",
                            letterSpacing: "1px",
                          }}
                        >
                          {t("invest.fund.risk")}
                          <span className='' style={{ color: getRiskColor(currentFund?.risk_factor), fontSize: "20px", marginInline: "6px" }}>
                            {currentFund?.risk_factor ? currentFund?.risk_factor.toUpperCase() : "Unavailable"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className=''
                  style={{
                    paddingInline: screenSize >= 576 ? "18px 0" : "0",
                  }}
                >
                  <div
                    className='row w-100 h-100 position-relative flex-column align-items-end justify-content-start text-start mx-auto p-4'
                    style={{
                      backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "var(--theme-card-border)",
                      color: theme === "dark" ? "rgba(255,255,255,.7)" : "rgba(0, 0, 0, 0.7)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {/* <div
                      className='position-absolute col-auto p-0'
                      style={{
                        top: "9px",
                        right: isRTL ? "" : "9px",
                        left: isRTL ? "9px" : ""
                      }}
                    >
                      <div
                        className='px-3'
                        style={{
                          backgroundImage: "linear-gradient( 69.2deg,  rgba(174, 71, 211, 1) 35.3%, rgba(141, 28, 182, 1) 100.3% )",
                          borderRadius: "20px",
                          fontSize: "15px",
                          paddingBlock: "4px",
                          color: "rgba(255,255,255,.8)",
                        }}
                      >
                        {navDate}
                      </div>
                    </div> */}
                    <div
                      className='position-absolute col-auto p-0'
                      style={{
                        bottom: "5px",
                        right: isRTL ? "" : "18px",
                        left: isRTL ? "18px" : "",
                        color: theme === 'dark' ? 'rgb(158 158 158)' : '',
                        fontSize: '14px'
                      }}
                    >
                      {t("invest.fund.as")}
                      <span style={{ color: theme === 'dark' ? 'rgb(211, 211, 211) ' : '', marginInlineStart: '6px' }}>{navDate}</span>
                    </div>
                    <div
                      className='col-12 p-0'
                      style={{
                        lineHeight: "1.1",
                        fontSize: "18px",
                      }}
                    >
                      {t("invest.fund.nav")}
                      <span
                        className='ms-2 ms-sm-3'
                        dir="ltr"
                        style={{
                          color: theme === "dark" ? "rgba(232, 147, 238, 1)" : "rgb(41, 124, 223)",
                          fontSize: "22px",
                        }}
                      >
                        <span
                          className=""
                          dir="ltr"
                          style={{
                            fontSize: "14px",
                            marginRight: '.25rem',
                            color: theme === 'dark' ? 'rgba(174, 174, 174, 1)' : '',
                          }}
                        >
                          {t("app.fund.rupee")}
                        </span>
                        {numberWithCommas(nav.whole) + "."}
                        <span
                          style={{
                            fontSize: "14px",
                          }}
                        >
                          {nav.decimal}
                        </span>
                      </span>
                    </div>
                    <div className='col-12 p-0'>
                      <hr style={{ color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.6)", marginBlock: ".75rem" }} />
                    </div>
                    <div
                      className='col-12 p-0'
                      style={{
                        lineHeight: "1.1",
                      }}
                    >
                      {t("invest.fund.offer")}
                      <span
                        className='ms-2 ms-sm-3'
                        dir="ltr"
                        style={{
                          color: theme === "dark" ? "rgba(232, 147, 238, 1)" : "rgb(41, 124, 223)",
                          fontSize: "22px",
                        }}
                      >
                        <span
                          className=""
                          dir="ltr"
                          style={{
                            fontSize: "14px",
                            marginRight: '.25rem',
                            color: theme === 'dark' ? 'rgba(174, 174, 174, 1)' : '',
                          }}
                        >
                          {t("app.fund.rupee")}
                        </span>
                        {numberWithCommas(offer.whole) + "."}
                        <span
                          style={{
                            fontSize: "14px",
                          }}
                        >
                          {offer.decimal}
                        </span>
                      </span>
                    </div>
                    <div className='col-12 p-0'>
                      <hr style={{ color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.6)", marginBlock: ".75rem" }} />
                    </div>
                    <div
                      className='col-12 p-0'
                      style={{
                        lineHeight: "1.1",
                      }}
                    >
                      {t("invest.fund.units")}
                      <span
                        className='ms-2 ms-sm-3'
                        style={{
                          color: theme === "dark" ? "rgba(232, 147, 238, 1)" : "rgb(41, 124, 223)",
                          fontSize: "22px",
                        }}
                      >
                        {numberWithCommas(unitsIssued.whole) + "."}
                        <span
                          style={{
                            fontSize: "14px",
                          }}
                        >
                          {unitsIssued.decimal}
                        </span>
                        <span
                          className="ms-1"
                          style={{
                            fontSize: "14px",
                            color: theme === 'dark' ? 'rgba(174, 174, 174, 1)' : '',
                          }}
                        >
                          {t("app.fund.units")}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className='mb-4 p-4 position-relative'
                style={{
                  backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                  borderRadius: "20px",
                  border: "1px solid",
                  borderColor: "var(--theme-card-border)",
                  minHeight: "440px",
                }}
              >
                <div className='w-100 d-flex align-items-center justify-content-start gap-2 mb-4 overflow-auto' style={{
                  scrollbarWidth: 'none'
                }}>
                  {["1M", "6M", "YTD", "1Y", "3Y", "5Y"].map((item, idx) => {
                    return (
                      <button
                        className='btn'
                        key={idx}
                        disabled={activeTimeRange === item}
                        type='button'
                        role='button'
                        onClick={() => setActiveTimeRange(item)}
                        style={{
                          ...stockTimeRangeButton(theme),
                        }}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>

                {loadingChart ? (
                  <div
                    className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                    style={{
                      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                    }}
                  >
                    <div
                      className=''
                      role='status'
                      style={{
                        fontSize: "1.2rem",
                      }}
                    >
                      Loading Chart Data ...
                    </div>
                    <div
                      className='spinner-border'
                      style={{
                        fontSize: "0.8rem",
                        height: "30px",
                        width: "30px",
                        color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                      }}
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {finalChartData !== null ? (
                      <>
                        {finalChartData.length !== 0 ? (
                          <FundPerformanceChart range={activeTimeRange} isModalOpen={isModalOpen} data={finalChartData} />
                        ) : (
                          <div
                            className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                            style={{
                              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                            }}
                          >
                            <div
                              className=''
                              role='status'
                              style={{
                                fontSize: "1.2rem",
                              }}
                            >
                              No Data Found
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </>
                )}
              </div>
              <div className='mb-5'>
                <div
                  className=''
                  style={{
                    display: "grid",
                    gridTemplateColumns: screenSize >= 992 ? "1fr 1fr 1fr 1fr" : screenSize <= 991 && screenSize >= 576 ? "1fr 1fr" : "1fr",
                    // gridTemplateRows: screenSize >= 992 ? '1fr' : (screenSize <= 991 && screenSize >= 576) ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
                    gap: "1.5rem",
                  }}
                >
                  {currentFund?.date_of_incorporation ? (
                    <div
                      className='p-4'
                      style={{
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: "16px",
                          color: theme === "dark" ? "rgba(255,255,255,.6)" : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {t("invest.fund.date")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "22px",
                          color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {dayjs(currentFund?.date_of_incorporation).format("DD-MMM-YYYY")}
                      </div>
                    </div>
                  ) : ""}
                  {currentFund?.dividend_period ? (
                    <div
                      className='p-4'
                      style={{
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: "16px",
                          color: theme === "dark" ? "rgba(255,255,255,.6)" : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {t("invest.fund.dividend")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "22px",
                          color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {currentFund?.dividend_period}
                      </div>
                    </div>
                  ) : ""}
                  {currentFund?.management_fee_percentage ? (
                    <div
                      className='p-4'
                      style={{
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: "16px",
                          color: theme === "dark" ? "rgba(255,255,255,.6)" : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {t("invest.fund.fee")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "22px",
                          color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {currentFund?.management_fee_percentage}
                        <span style={{ color: "rgb(216, 117, 223)", marginLeft: "6px", fontSize: "16px" }}>%</span>
                      </div>
                    </div>
                  ) : ""}
                  {currentFund?.sale_load_percentage ? (
                    <div
                      className='p-4'
                      style={{
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: "16px",
                          color: theme === "dark" ? "rgba(255,255,255,.6)" : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {t("invest.fund.sales")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "22px",
                          color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {currentFund?.sale_load_percentage}
                        <span style={{ color: "rgb(216, 117, 223)", marginLeft: "6px", fontSize: "16px" }}>%</span>
                      </div>
                    </div>
                  ) : ""}
                  {currentFund?.backend_load_percentage ? (
                    <div
                      className='p-4'
                      style={{
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: "16px",
                          color: theme === "dark" ? "rgba(255,255,255,.6)" : "rgba(0, 0, 0, 0.5)",
                        }}
                      >
                        {t("invest.fund.back")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "22px",
                          color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {currentFund?.backend_load_percentage}
                        <span style={{ color: "rgb(216, 117, 223)", marginLeft: "6px", fontSize: "16px" }}>%</span>
                      </div>
                    </div>
                  ) : ""}
                </div>
              </div>
              {closeButton ? (
                <div className="w-100 d-flex align-items-center justify-content-between flex-nowrap text-center">
                  <div className="d-flex align-items-center gap-2 flex-nowrap">
                    <button
                      type='button'
                      className='btn btn-primary border-0 p-0 px-3 d-inline-flex align-items-center justify-content-center'
                      onClick={() => {
                        closeButton();

                        navigate("/invest", { replace: true });

                        if (!user) {
                          navigate("/unlock");
                          return;
                        }

                        if (user && user?.requiresDocumentVerification) {
                          navigate("/verify");
                          return;
                        }

                        navigate(`/invest?modal=purchase&symbol=${currentFund?.symbol_code}`)
                        setMenu("invest")
                      }}
                      style={{
                        minHeight: "38px",
                        minWidth: "80px",
                        fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                      }}
                    >
                        {t("app.fund.purchase")}
                    </button>
                    {hasBalanceUnits ? (
                      <>
                        <button
                          type='button'
                          className='btn btn-primary border-0 p-0 px-3 d-inline-flex align-items-center justify-content-center'
                          onClick={() => {
                            closeButton();

                            // if (!user) {
                            //   navigate("/unlock");
                            //   return;
                            // }

                            // if (user && user?.requiresDocumentVerification) {
                            //   navigate("/verify");
                            //   return;
                            // }

                            navigate(`/portfolio?modal=redeem&symbol=${currentFund?.symbol_code}`)
                            setMenu("portfolio")
                            }
                          }
                          style={{
                            minHeight: "38px",
                            minWidth: "80px",
                            fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                          }}
                        >
                            {t("portfolio.fund.redeem")}
                        </button>
                        <button
                          type='button'
                          className='btn btn-primary border-0 p-0 px-3 d-inline-flex align-items-center justify-content-center'
                          onClick={() => {
                            closeButton();

                            // if (!user) {
                            //   navigate("/unlock");
                            //   return;
                            // }

                            // if (user && user?.data?.documentsVerified) {
                            //   navigate("/unlock");
                            //   return;
                            // }

                            navigate(`/portfolio?modal=convert&symbol=${currentFund?.symbol_code}`)
                            setMenu("portfolio")
                            }
                          }
                          style={{
                            minHeight: "38px",
                            minWidth: "80px",
                            fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                          }}
                        >
                            {t("portfolio.fund.convert")}
                        </button>
                      </>
                    ) : ""}
                  </div>
                  <button
                    type='button'
                    className='btn border-0 p-0 px-3 d-inline-flex align-items-center justify-content-center'
                    onClick={closeButton}
                    style={{
                      minHeight: "38px",
                      minWidth: "80px",
                      fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                      ...customModalCancelButton
                    }}
                  >
                      {t("modals.common.close")}
                  </button>
                </div>
              ) : ""}
            </>
          )}
        </div>
        {}
      </div>
    </div>
  );
}
